// The engagement client profile — the answers Engagement Registration,
// Management → New / Edit Engagement, Engagement Amendment and the Screening
// request / record ask (docs/modules/engagements.md). Shared by the forms and
// the server (CommonJS for the same reason as qcQuestions.js).
//
// A form covering several companies may answer any of these per company
// ("Assign per entity" under the question): the saved entity row then carries
// `profile` = { <field>: value } holding only the fields that company answers
// differently, and the request's own answer is the first company's. Ultimate
// Parent is the one profile field that is always shared.
const { PIE_TYPE_OPTIONS, PIE_TYPE_OTHERS } = require('../pages/qcQuestions');

// Field groups that split together — one "Assign per entity" link per group.
// `fields` are the keys of a saved `profile` (the API / master spelling).
const PROFILE_SPLIT_GROUPS = [
  { key: 'service', label: 'Service Provided', fields: ['serviceProvided', 'serviceOther'] },
  { key: 'descriptionOfService', label: 'Description of Service', fields: ['descriptionOfService'] },
  { key: 'pie', label: 'Public Interest Entity (PIE)', fields: ['isPie', 'pieType', 'pieTypeOther'] },
  { key: 'ssicPrimary', label: 'SSIC Code Primary', fields: ['ssicPrimary'] },
  { key: 'ssicSecondary', label: 'SSIC Code Secondary', fields: ['ssicSecondary'] },
  { key: 'principalActivity1', label: 'Principal Activity 1', fields: ['principalActivity1'] },
  { key: 'principalActivity2', label: 'Principal Activity 2', fields: ['principalActivity2'] },
  { key: 'overseasOperations', label: 'Overseas Operations', fields: ['overseasOperations'] },
  { key: 'consolidatedFs', label: 'Consolidated Financial Statements', fields: ['consolidatedFs'] },
  { key: 'secRegistered', label: 'SEC Registered', fields: ['secRegistered'] },
  { key: 'isGroupAudit', label: 'Group Audit', fields: ['isGroupAudit'] },
  { key: 'underInvestigation', label: 'Under Investigation', fields: ['underInvestigation'] },
  { key: 'tcwgFeeCommunicated', label: 'Fee Information Communicated to TCWG', fields: ['tcwgFeeCommunicated'] },
];
const PROFILE_FIELDS = PROFILE_SPLIT_GROUPS.flatMap((g) => g.fields);
const PROFILE_BOOL_FIELDS = ['isPie', 'overseasOperations', 'secRegistered', 'isGroupAudit', 'underInvestigation', 'tcwgFeeCommunicated'];
const groupOfField = (field) => PROFILE_SPLIT_GROUPS.find((g) => g.fields.includes(field)) || null;

const PIE_LABEL = 'Public Interest Entity (PIE)?';
const PIE_TYPE_LABEL = 'Type of PIE';
const PIE_TYPE_OTHER_LABEL = 'Type of PIE (if Others)';
const LISTED_PIE_TYPE = 'Publicly traded entity';

/** "Yes — Financial Institution" / "Yes — Others (…)" / "No" / null (unanswered). */
const pieText = ({ isPie, pieType, pieTypeOther } = {}) => {
  if (isPie === true) {
    if (!pieType) return 'Yes';
    return pieType === PIE_TYPE_OTHERS && pieTypeOther ? `Yes — Others (${pieTypeOther})` : `Yes — ${pieType}`;
  }
  if (isPie === false) return 'No';
  return null;
};

/** Listed company = PIE Yes with the publicly traded type (Late Accounts threshold). */
const isListedOf = ({ isPie, pieType } = {}) => isPie === true && pieType === LISTED_PIE_TYPE;

/** The effective service text of a profile-like object (Other → the typed text). */
const effectiveServiceOf = (p) => (String((p || {}).serviceProvided || '').trim() === 'Other'
  ? String((p || {}).serviceOther || '').trim() || 'Other'
  : String((p || {}).serviceProvided || '').trim());

/** A request / record row merged with one company's own answers. */
const profileForEntity = (base, entity) => Object.assign({}, base, (entity && entity.profile) || {});

/** Whether any company on the request answers this group on its own. */
const groupSplit = (entities, group) => (entities || []).length > 1
  && (entities || []).some((e) => e.profile && group.fields.some((f) => e.profile[f] !== undefined));

/** One group's value of a profile-like object as display text. */
const groupText = (group, p) => {
  if (group.key === 'service') return effectiveServiceOf(p) || null;
  if (group.key === 'pie') return pieText(p);
  const v = p[group.fields[0]];
  if (v === true) return 'Yes';
  if (v === false) return 'No';
  if (group.key === 'consolidatedFs') return v === 'yes' ? 'Yes' : v === 'no' ? 'No' : null;
  return v === undefined || v === null || String(v).trim() === '' ? null : String(v);
};

/** "Company: value; Company: value" for a split group, or null when not split. */
const groupLine = (request, group) => {
  const entities = (request && request.entities) || [];
  if (!groupSplit(entities, group)) return null;
  return entities.map((e) => `${e.entityName}: ${groupText(group, profileForEntity(request, e)) || '—'}`).join('; ');
};

// History / return-comment labels of the PIE answers and of every group's
// per-company row (`<group>Split`) — spread into each detail page's labels.
const PROFILE_CHANGE_LABELS = Object.assign(
  { isPie: PIE_LABEL, pieType: PIE_TYPE_LABEL, pieTypeOther: PIE_TYPE_OTHER_LABEL },
  Object.fromEntries(PROFILE_SPLIT_GROUPS.map((g) => [`${g.key}Split`, `${g.label} per Company`]))
);

module.exports = {
  PROFILE_SPLIT_GROUPS, PROFILE_FIELDS, PROFILE_BOOL_FIELDS, PROFILE_CHANGE_LABELS, groupOfField,
  PIE_TYPE_OPTIONS, PIE_TYPE_OTHERS, PIE_LABEL, PIE_TYPE_LABEL, PIE_TYPE_OTHER_LABEL, LISTED_PIE_TYPE,
  pieText, isListedOf, effectiveServiceOf, profileForEntity, groupSplit, groupText, groupLine,
};
